/**
 * Zod-Schemas für alle schreibenden API-Aufrufe.
 *
 * Der Server validiert jeden Request-Body damit, das Web leitet seine Formulartypen
 * daraus ab (`z.infer`). Ein Feld, das hier nicht steht, kommt nicht in die Datenbank.
 *
 * Fehlermeldungen sind deutsch und in Du-Form formuliert — sie landen unverändert
 * im Formular unter dem jeweiligen Feld.
 */
import { z } from 'zod';
import { FEATURE_STATUSES, PRODUCT_STATUSES, ROADMAP_ITEM_STATUSES, ROLES, TASK_KINDS, TASK_STATUSES, USER_STATUSES } from './enums.js';
import { CRITERION_MAX, CRITERION_MIN } from './scoring.js';
// --- Bausteine ------------------------------------------------------------------
const trimmedText = (max) => z.string().trim().max(max);
const requiredText = (max, feld) => trimmedText(max).min(1, { message: `${feld} darf nicht leer sein.` });
/** Optionales Freitextfeld: leerer String und `null` bedeuten beide „nicht gesetzt". */
const optionalText = (max) => trimmedText(max)
    .nullish()
    .transform((value) => (value ? value : null));
export const idSchema = z.string().uuid({ message: 'Ungültige ID.' });
/** ISO-Datum (`2027-03-31`) — die API tauscht Daten ausschließlich in dieser Form. */
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Bitte ein Datum im Format JJJJ-MM-TT angeben.' })
    .refine((value) => !Number.isNaN(Date.parse(value)), { message: 'Kein gültiges Datum.' });
// --- Authentifizierung ----------------------------------------------------------
export const loginSchema = z.object({
    email: z
        .string()
        .trim()
        .toLowerCase()
        .email({ message: 'Bitte eine gültige E-Mail-Adresse eingeben.' }),
    password: z.string().min(1, { message: 'Bitte gib dein Passwort ein.' })
});
/**
 * Passwortregeln bewusst schlank: Laenge schlägt Zeichenklassen-Akrobatik. 12 Zeichen
 * Minimum, keine erzwungenen Sonderzeichen — das erzeugt sonst nur `Passwort1!`.
 */
export const passwordSchema = z
    .string()
    .min(12, { message: 'Das Passwort muss mindestens 12 Zeichen lang sein.' })
    .max(200, { message: 'Das Passwort darf höchstens 200 Zeichen lang sein.' });
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, { message: 'Bitte gib dein aktuelles Passwort ein.' }),
    newPassword: passwordSchema
});
// --- Benutzer (Spec §5) ---------------------------------------------------------
export const userCreateSchema = z.object({
    name: requiredText(120, 'Der Name'),
    email: z
        .string()
        .trim()
        .toLowerCase()
        .email({ message: 'Bitte eine gültige E-Mail-Adresse eingeben.' }),
    password: passwordSchema,
    role: z.enum(ROLES),
    status: z.enum(USER_STATUSES).default('active')
});
export const userUpdateSchema = z
    .object({
    name: requiredText(120, 'Der Name').optional(),
    email: z
        .string()
        .trim()
        .toLowerCase()
        .email({ message: 'Bitte eine gültige E-Mail-Adresse eingeben.' })
        .optional(),
    role: z.enum(ROLES).optional(),
    status: z.enum(USER_STATUSES).optional(),
    /** Nur der Administrator setzt hier ein neues Passwort (Zuruecksetzen). */
    password: passwordSchema.optional()
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Es wurde nichts geändert.'
});
// --- Produkte (Spec §6) ---------------------------------------------------------
export const productCreateSchema = z.object({
    name: requiredText(160, 'Der Produktname'),
    shortDescription: optionalText(280),
    description: optionalText(20_000),
    /** Technischer Steckbrief (Spec §6): Freitext, siehe Kommentar am Prisma-Modell. */
    techStack: optionalText(10_000),
    ownerId: idSchema.nullish().transform((value) => value ?? null),
    /**
     * Übergeordnetes Produkt für Unterprodukte (Plugins u. Ä.). `null` heißt
     * „eigenständig". Ob das gewählte Produkt überhaupt Elternteil sein DARF — nur eine
     * Ebene, kein Selbstbezug — prüft der Server, nicht das Schema: Es braucht dafür den
     * Blick in die Datenbank, den ein Zod-Schema nicht hat.
     */
    parentId: idSchema.nullish().transform((value) => value ?? null),
    status: z.enum(PRODUCT_STATUSES).default('development'),
    category: optionalText(80),
    version: optionalText(40)
});
export const productUpdateSchema = productCreateSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Es wurde nichts geändert.' });
// --- Features (Spec §8) ---------------------------------------------------------
export const featureCreateSchema = z.object({
    productId: idSchema,
    title: requiredText(200, 'Der Titel'),
    description: optionalText(20_000),
    status: z.enum(FEATURE_STATUSES).default('idea'),
    ownerId: idSchema.nullish().transform((value) => value ?? null),
    targetVersion: optionalText(40)
});
/**
 * `productId` fehlt hier absichtlich: ein Feature wechselt nicht das Produkt. Der
 * Status läuft über die eigene Route `PATCH /api/features/:id/status`, damit die
 * Workflow-Prüfung nicht am normalen Bearbeiten vorbeigeht.
 */
export const featureUpdateSchema = featureCreateSchema
    .omit({ productId: true, status: true })
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Es wurde nichts geändert.' });
export const featureStatusSchema = z.object({
    status: z.enum(FEATURE_STATUSES)
});
// --- Kommentare -----------------------------------------------------------------
/**
 * 5000 Zeichen sind reichlich für eine fachliche Rückmeldung und immer noch weit
 * unter dem, was ein versehentlich eingefügtes Dokument mitbrächte.
 */
export const commentSchema = z.object({
    body: requiredText(5000, 'Der Kommentar')
});
// --- Aufgaben an einem Feature --------------------------------------------------
export const taskCreateSchema = z.object({
    assigneeId: idSchema,
    kind: z.enum(TASK_KINDS),
    /** Optionaler Zusatz — was genau getan werden soll. */
    note: optionalText(500)
});
/** Erledigen oder wieder öffnen — mehr lässt sich an einer Aufgabe nicht ändern. */
export const taskUpdateSchema = z.object({
    status: z.enum(TASK_STATUSES)
});
// --- Bewertung (Spec §9) --------------------------------------------------------
const criterion = (feld) => z
    .number({ message: `Bitte bewerte „${feld}".` })
    .int({ message: `„${feld}" muss eine ganze Zahl sein.` })
    .min(CRITERION_MIN, {
    message: `„${feld}" liegt zwischen ${CRITERION_MIN} und ${CRITERION_MAX}.`
})
    .max(CRITERION_MAX, {
    message: `„${feld}" liegt zwischen ${CRITERION_MIN} und ${CRITERION_MAX}.`
});
export const evaluationSchema = z.object({
    customerValue: criterion('Kundennutzen'),
    strategicValue: criterion('Strategische Bedeutung'),
    urgency: criterion('Dringlichkeit'),
    technicalValue: criterion('Technischer Nutzen'),
    effort: criterion('Entwicklungsaufwand')
});
// --- Roadmap (Spec §10) ---------------------------------------------------------
const roadmapBase = z.object({
    featureId: idSchema,
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    status: z.enum(ROADMAP_ITEM_STATUSES).default('planned')
});
const endsAfterStart = (value) => !value.startDate || !value.endDate || value.startDate <= value.endDate;
export const roadmapItemCreateSchema = roadmapBase.refine(endsAfterStart, {
    message: 'Das Ende darf nicht vor dem Beginn liegen.',
    path: ['endDate']
});
export const roadmapItemUpdateSchema = roadmapBase
    .omit({ featureId: true })
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Es wurde nichts geändert.' })
    .refine(endsAfterStart, {
    message: 'Das Ende darf nicht vor dem Beginn liegen.',
    path: ['endDate']
});
// --- Einstellungen (Spec §9: „Die Gewichtungen müssen später konfigurierbar sein") --
const weight = z
    .number({ message: 'Bitte eine Zahl angeben.' })
    .min(0, { message: 'Gewichte dürfen nicht negativ sein.' })
    .max(10, { message: 'Gewichte über 10 sind nicht vorgesehen.' });
export const weightsSchema = z.object({
    customerValue: weight,
    strategicValue: weight,
    technicalValue: weight,
    urgency: weight,
    effort: weight
});
export const settingsUpdateSchema = z.object({
    weights: weightsSchema
});
//# sourceMappingURL=schemas.js.map